import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Tabella } from '../../componenti/base.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'

/**
 * Unisci enti (475; email «Sugli ENTI» della Direzione del 25/10/2026, punto 2). Dalla pagina Enti
 * «Unisci i doppioni» propone i gruppi di enti probabilmente uguali; dalla scheda dell'ente «Unisci con
 * un altro ente» cerca l'altro. In tutti e due i casi si sceglie l'ente da tenere, si legge l'anteprima
 * (che cosa passa, quali dati si riempiono, che cosa e' doppio) e si conferma. Le decisioni e i testi
 * sono dell'API (rotte/unisci-enti.ts): qui si mostrano.
 */

type EnteBreve = {
  id: string; nome: string; sigla: string | null; etichetta: string; luogo: string | null
  cf_piva: string | null; attivo: boolean; legacy_id: number | null
}
type Conteggio = { tabella: string; colonna: string; nome: string; righe: number; passano: number; tolte: number; nota: string | null }
type Campo = { campo: string; nome: string; esito: 'riempito' | 'diverso'; tenuto: string | null; unito: string | null }
type Anteprima = {
  tenuto: EnteBreve; unito: EnteBreve; conteggi: Conteggio[]; totale: number; campi: Campo[]
  doppioni: string[]; ostacoli: string[]; avvisi: string[]; impronta: string
}
export type VoceDoppione = {
  id: string; nome: string; sigla: string | null; cf_piva: string | null; comune_nome: string | null
  provincia_sigla: string | null; attivo: boolean; usi: number; legacy_id: number | null
}
export type Gruppo = {
  motivi: ('stesso_codice_fiscale' | 'stesso_nome' | 'nomi_simili' | 'stesso_acronimo')[]
  proposto: string; enti: VoceDoppione[]
}
type Trovato = { id: string; nome: string; sigla: string | null; attivo: boolean; comune_nome: string | null }

const MOTIVI: Record<Gruppo['motivi'][number], string> = {
  stesso_codice_fiscale: 'stesso codice fiscale o partita IVA',
  stesso_nome: 'stesso nome',
  nomi_simili: 'nomi quasi uguali',
  stesso_acronimo: 'stesso acronimo',
}
const ESITI: Record<Campo['esito'], string> = {
  riempito: 'questo ente non lo ha: prende il valore dell’altro',
  diverso: 'resta quello di questo ente',
}
const quanti = (n: number, uno: string, tanti: string) => (n === 1 ? `1 ${uno}` : `${n} ${tanti}`)
const nomeDi = (e: { nome: string; sigla: string | null }) => `${e.sigla ? `${e.sigla} — ` : ''}${e.nome}`
const luogoDi = (e: { comune_nome: string | null; provincia_sigla?: string | null }) =>
  (e.comune_nome ? `${e.comune_nome}${e.provincia_sigla ? ` (${e.provincia_sigla})` : ''}` : null)

/** I probabili doppioni dell'anagrafica, un gruppo per riga, da rivedere e unire. */
export function ProposteDoppioniEnti({ chiudi, rivedi }: { chiudi: () => void; rivedi: (g: Gruppo) => void }) {
  const [gruppi, setGruppi] = useState<Gruppo[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    api.get<{ gruppi: Gruppo[] }>('/enti/doppioni').then((r) => setGruppi(r.gruppi))
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])

  return (
    <Modale larga titolo="Probabili doppioni" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>}>
      <p className="piccolo">
        Enti che probabilmente sono lo stesso, registrati più volte. Nessuno si unisce da solo: apri «Rivedi», scegli
        quale tenere e conferma. Se due enti sono lo stesso ma qui non compaiono, apri la scheda di quello da tenere e
        usa «Unisci con un altro ente».
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!gruppi && !errore && <p className="tenue">Cerco i doppioni…</p>}
      {gruppi?.length === 0 && <p>Nessun probabile doppione fra gli enti che gestisci.</p>}
      {gruppi && gruppi.length > 0 && (
        <ul className="mediazioni-elenco-unione">
          {gruppi.map((g) => (
            <li key={g.enti.map((e) => e.id).join()} className="riga">
              <span>
                <strong>{g.enti.map(nomeDi).join(' · ')}</strong>
                <span className="piccolo tenue"> — {g.motivi.map((m) => MOTIVI[m]).join(', ')}</span>
              </span>
              <span className="spinta">
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => rivedi(g)}>Rivedi</Bottone>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modale>
  )
}

/**
 * Un gruppo di probabili doppioni: si sceglie l'ente da tenere e si uniscono gli altri uno alla volta,
 * ciascuno con la sua anteprima.
 */
export function UnisciGruppo({ gruppo, chiudi, fatto }: {
  gruppo: Gruppo; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [tenere, setTenere] = useState(gruppo.proposto)
  const [enti, setEnti] = useState(gruppo.enti)
  const [unire, setUnire] = useState<string | null>(null)
  const [uniti, setUniti] = useState<string[]>([])
  const tenuto = enti.find((e) => e.id === tenere)!

  if (unire) {
    return (
      <AnteprimaUnione tenuto={tenere} unito={unire} chiudi={chiudi} indietro={() => setUnire(null)}
                       fatta={(nome) => {
                         const restano = enti.filter((e) => e.id !== unire)
                         const tutti = [...uniti, nome]
                         setUniti(tutti); setEnti(restano); setUnire(null)
                         if (restano.length === 1) {
                           fatto(`Fatto: ${tutti.map((n) => `«${n}»`).join(', ')} ${tutti.length === 1 ? 'è unito' : 'sono uniti'} `
                             + `a «${nomeDi(tenuto)}».`)
                         }
                       }} />
    )
  }
  return (
    <Modale larga titolo="Unisci i doppioni" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>{uniti.length ? 'Fine' : 'Lascia stare'}</Bottone>}>
      {uniti.length > 0 && <Avviso tipo="successo">Unit{uniti.length === 1 ? 'o' : 'i'} a «{nomeDi(tenuto)}»: {uniti.join(', ')}.</Avviso>}
      <p className="piccolo">
        Scegli l’ente da <strong>tenere</strong>, poi controlla l’unione di ciascuno degli altri: tutto quello che li
        nomina passa all’ente tenuto, e i loro nomi restano come «Anche: …».
      </p>
      <fieldset className="gruppo-caselle">
        <legend>Enti</legend>
        {enti.map((e) => (
          <div key={e.id} className="mediazioni-riga-unione riga">
            <label className="riga">
              <input type="radio" name="tenere" checked={tenere === e.id} disabled={uniti.length > 0}
                     onChange={() => setTenere(e.id)} />
              <span>
                <strong>{nomeDi(e)}</strong>{' '}
                {!e.attivo && <Etichetta>disattivato</Etichetta>}
                <span className="piccolo tenue">
                  {luogoDi(e) ? ` · ${luogoDi(e)}` : ''}{e.cf_piva ? ` · ${e.cf_piva}` : ''}
                  {e.legacy_id !== null ? ` · vecchio archivio n. ${e.legacy_id}` : ''}
                  {` · ${quanti(e.usi, 'riga lo nomina', 'righe lo nominano')}`}
                </span>
              </span>
            </label>
            {e.id !== tenere && (
              <span className="spinta">
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => setUnire(e.id)}>
                  Controlla l’unione
                </Bottone>
              </span>
            )}
          </div>
        ))}
      </fieldset>
    </Modale>
  )
}

/**
 * «Unisci con un altro ente» dalla scheda dell'ente da tenere: si cerca l'altro per nome o acronimo,
 * anche fra i disattivati, e si apre l'anteprima.
 */
export function UnisciConEnte({ ente, proposta, chiudi, fatto }: {
  ente: { id: string; nome: string; sigla: string | null }
  /** Ente da unire gia' scelto («Tieni invece questo» dall'anteprima aperta sull'altro ente). */
  proposta?: string | null
  chiudi: () => void
  fatto: (testo: string) => void
}) {
  const [cerca, setCerca] = useState('')
  const [trovati, setTrovati] = useState<Trovato[]>([])
  const [unire, setUnire] = useState<string | null>(proposta ?? null)

  useEffect(() => {
    const testo = cerca.trim()
    if (testo.length < 2) { setTrovati([]); return }
    let vivo = true
    const t = setTimeout(() => {
      const percorso = (attivo: string) => `/enti?${new URLSearchParams({ cerca: testo, attivo, limite: '10' })}`
      Promise.all([api.get<Trovato[]>(percorso('true')), api.get<Trovato[]>(percorso('false'))])
        .then(([attivi, disattivati]) => {
          if (vivo) setTrovati([...attivi, ...disattivati].filter((x) => x.id !== ente.id))
        })
        .catch(() => { if (vivo) setTrovati([]) })
    }, 300)
    return () => { vivo = false; clearTimeout(t) }
  }, [cerca, ente.id])

  if (unire) {
    return (
      <AnteprimaUnione tenuto={ente.id} unito={unire} chiudi={chiudi} indietro={() => setUnire(null)} invertibile
                       fatta={(nome) => fatto(`«${nome}» è stato unito a questo ente: tutto quello che lo nominava ora è qui.`)} />
    )
  }
  return (
    <Modale larga titolo="Unisci con un altro ente" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>}>
      <p className="piccolo">
        Si tiene <strong>questo ente</strong> ({nomeDi(ente)}): cerca l’altra registrazione dello stesso ente. Prima di
        unire vedrai che cosa passa e quali dati cambiano.
      </p>
      <Campo tipo="search" etichetta="Ente da unire" valore={cerca} cambia={setCerca} maxLength={120}
             aiuto="Nome o acronimo, anche di un ente disattivato." />
      {trovati.length > 0 && (
        <ul className="mediazioni-elenco-unione">
          {trovati.map((t) => (
            <li key={t.id} className="riga">
              <span>
                {nomeDi(t)}
                {!t.attivo && <span className="piccolo tenue"> · disattivato</span>}
                {t.comune_nome && <span className="piccolo tenue"> · {t.comune_nome}</span>}
              </span>
              <span className="spinta">
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => setUnire(t.id)}>
                  Controlla l’unione
                </Bottone>
              </span>
            </li>
          ))}
        </ul>
      )}
      {cerca.trim().length >= 2 && !trovati.length && <p className="tenue">Nessun ente trovato.</p>}
    </Modale>
  )
}

/** Anteprima di un'unione e conferma, con l'impronta dell'anteprima letta. */
function AnteprimaUnione({ tenuto, unito, chiudi, indietro, fatta, invertibile = false }: {
  tenuto: string; unito: string; chiudi: () => void; indietro: () => void
  fatta: (nome: string) => void
  /** Dalla scheda dell'ente: si puo' tenere l'altro, aprendo la sua scheda. */
  invertibile?: boolean
}) {
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function controlla() {
    setErrore(null)
    try {
      setAnteprima(await api.post<Anteprima>(`/enti/${tenuto}/unisci/anteprima`, { unito }))
    } catch (e) {
      setErrore(messaggioErrore(e))
    }
  }
  useEffect(() => { void controlla() }, [tenuto, unito]) // eslint-disable-line react-hooks/exhaustive-deps

  async function unisci() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/enti/${tenuto}/unisci`, { unito, impronta: anteprima.impronta })
      fatta(nomeDi(anteprima.unito))
    } catch (e) {
      setErrore(messaggioErrore(e))
      if (e instanceof ErroreApi && e.codice === 'ANTEPRIMA_CAMBIATA') void controlla()
    } finally {
      setInCorso(false)
    }
  }

  if (!anteprima) {
    return (
      <Modale larga titolo="Unisci gli enti" chiudi={chiudi}
              azioni={<Bottone variante="discreto" onClick={indietro}>Torna alla scelta</Bottone>}>
        {errore ? <Avviso tipo="errore">{errore}</Avviso> : <p className="tenue">Preparo l’anteprima…</p>}
      </Modale>
    )
  }
  const { tenuto: t, unito: u } = anteprima
  const passano = anteprima.conteggi.filter((c) => c.passano > 0)
  const restano = anteprima.conteggi.filter((c) => c.passano === 0)
  const descrivi = (e: EnteBreve) => [e.luogo, e.cf_piva, e.legacy_id !== null ? `vecchio archivio n. ${e.legacy_id}` : null,
    e.attivo ? null : 'disattivato'].filter(Boolean).join(' · ')
  return (
    <Modale larga titolo={`Unire «${nomeDi(u)}» a «${nomeDi(t)}»?`} chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={indietro}>Torna alla scelta</Bottone>
              {invertibile && (
                <Link className="bottone bottone-discreto" to={`/enti/${u.id}?unisci=${t.id}`} onClick={chiudi}>
                  Tieni invece l’altro
                </Link>
              )}
              <Bottone variante="critico" inCorso={inCorso} disabled={anteprima.ostacoli.length > 0}
                       onClick={() => void unisci()}>
                Unisci gli enti
              </Bottone>
            </>}>
      <dl className="persona-intestazione">
        <div className="persona-intestazione-voce persona-intestazione-larga">
          <dt>Si tiene</dt>
          <dd><strong>{nomeDi(t)}</strong> <span className="tenue">{descrivi(t)}</span></dd>
        </div>
        <div className="persona-intestazione-voce persona-intestazione-larga">
          <dt>Si unisce</dt>
          <dd><strong>{nomeDi(u)}</strong> <span className="tenue">{descrivi(u)}</span></dd>
        </div>
      </dl>

      {anteprima.ostacoli.length > 0 && (
        <Avviso tipo="errore">
          <strong>Gli enti non si possono unire.</strong>
          <ul className="elenco-avviso">{anteprima.ostacoli.map((o) => <li key={o}>{o}</li>)}</ul>
        </Avviso>
      )}

      <h3>Che cosa passa all’ente tenuto</h3>
      {passano.length
        ? (
          <ul className="elenco-avviso">
            {passano.map((c) => (
              <li key={`${c.tabella}.${c.colonna}`}>
                {c.nome}: {c.passano}
                {c.tolte > 0 && !c.nota && <span className="tenue"> ({quanti(c.tolte, 'già presente non passa', 'già presenti non passano')})</span>}
                {c.nota && <span className="tenue"> · {c.nota}</span>}
              </li>
            ))}
          </ul>
        )
        : <p className="tenue">Nessun servizio, referente, progetto o altro dato collegato all’altro ente.</p>}
      {restano.length > 0 && (
        <p className="piccolo tenue">
          Non passano: {restano.map((c) => `${c.nome} (${c.righe}${c.nota ? `, ${c.nota}` : ''})`).join('; ')}.
        </p>
      )}

      <h3>Dati anagrafici</h3>
      {anteprima.campi.length
        ? (
          <Tabella intestazioni={['Campo', 'Ente tenuto', 'L’altro ente', 'Che cosa succede']}>
            {anteprima.campi.map((c) => (
              <tr key={c.campo}>
                <td data-etichetta="Campo">{c.nome}</td>
                <td data-etichetta="Ente tenuto">{c.tenuto ?? <span className="tenue">—</span>}</td>
                <td data-etichetta="L’altro ente">{c.unito ?? <span className="tenue">—</span>}</td>
                <td data-etichetta="Che cosa succede">
                  <Etichetta tono={c.esito === 'riempito' ? 'informativo' : 'neutro'}>{ESITI[c.esito]}</Etichetta>
                </td>
              </tr>
            ))}
          </Tabella>
        )
        : <p className="tenue">I dati anagrafici coincidono, oppure l’altro ente non ne ha in più.</p>}

      {anteprima.doppioni.length > 0 && (
        <>
          <h3>Presenti in tutti e due</h3>
          <ul className="elenco-avviso">{anteprima.doppioni.map((d) => <li key={d}>{d}</li>)}</ul>
        </>
      )}
      {anteprima.avvisi.length > 0 && (
        <Avviso>
          <ul className="elenco-avviso">{anteprima.avvisi.map((a) => <li key={a}>{a}</li>)}</ul>
        </Avviso>
      )}
      <p className="piccolo tenue">
        L’altro ente non si cancella: resta disattivato come rimando a questo, non compare più negli elenchi e nelle
        tendine, chi lo apre arriva qui, e il suo nome resta come «Anche: …» e si trova con la ricerca. L’unione non si
        annulla dalla pagina; i valori di prima restano registrati.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
